import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Divider,
  Message,
  Modal,
  List,
  Image
} from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import userPuzzle from '../../assets/userPuzzle.svg';
import voiceActressBW from '../../assets/micAndshield.svg';
import bookStack from '../../assets/justBookStack.svg';
import {
  FormikInput,
  FormikSelect,
  FormikField
} from '../../pages/subPages/voiceActor/FormComponents';
import apiClient from '../../apiTools/apiClient';

// Validation schema for the add user form
const addUserValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Password confirmation is required'),
  firstName: Yup.string()
    .min(2, 'First name must be at least 2 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  phone: Yup.string()
    .matches(/^[\d\s\-\+\(\)]*$/, 'Invalid phone number format')
    .nullable(),
  userType: Yup.string()
    .oneOf(['admin', 'bookCreator', 'voiceActor'], 'Invalid user type')
    .required('User type is required')
});

const AddUser = () => {
  const userProfile = useSelector((state) => state.user.profile);
  const navigate = useNavigate();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 12.5;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 12.5;
    return strength;
  };

  // Handle password change for strength indicator
  const handlePasswordChange = (e, setFieldValue) => {
    const password = e.target.value;
    setFieldValue('password', password);
    setShowPasswordStrength(password.length > 0);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  // User type data with descriptions and capabilities
  const userTypeData = [
    {
      value: 'admin',
      label: 'Administrator',
      icon: 'shield',
      color: 'red',
      image: userPuzzle,
      description: 'Full system access and management capabilities',
      capabilities: [
        'Manage all users and permissions',
        'Access system configuration',
        'View all content and analytics',
        'Create and manage Book Creators and Voice Actors',
        'Access security and path registry'
      ],
      exampleUser: {
        name: 'John Smith',
        role: 'System Administrator',
        specialties: 'User Management, System Configuration, Security'
      }
    },
    {
      value: 'bookCreator',
      label: 'Book Creator',
      icon: 'book',
      color: 'green',
      image: bookStack,
      description: 'Create and manage book projects',
      capabilities: [
        'Create and edit book projects',
        'Manage book content and chapters',
        'Post voice acting opportunities',
        'Review and approve voice actor applications',
        'Access book analytics'
      ],
      exampleUser: {
        name: 'Jane Doe',
        role: 'Author',
        specialties: 'Fiction Writing, Children\'s Books, Poetry'
      }
    },
    {
      value: 'voiceActor',
      label: 'Voice Actor',
      icon: 'microphone',
      color: 'blue',
      image: voiceActressBW,
      description: 'Provide voice narration for books',
      capabilities: [
        'Browse and apply for voice opportunities',
        'Upload voice samples and demos',
        'Manage voice actor profile',
        'Record and submit auditions',
        'Track application status'
      ],
      exampleUser: {
        name: 'Sarah Johnson',
        role: 'Professional Voice Actor',
        specialties: 'Audiobooks, Character Voices, Narration'
      }
    }
  ];

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitError(null);

    try {
      // Prepare the user data for submission
      const userData = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        name: `${values.firstName} ${values.lastName}`,
        phone: values.phone || '',
        primaryRole: values.userType,
        role: values.userType === 'admin' ? 'ADMIN' :
          values.userType === 'bookCreator' ? 'BOOK_CREATOR' :
            'VOICE_ACTOR',
        isActive: true
      };

      // Make API call to create user with extended timeout
      const response = await apiClient.post('/admin/create-user', userData, {
        timeout: 60000 // 60 seconds timeout for profile creation
      });

      if (response.data.success) {
        setCreatedUser({
          ...userData,
          userId: response.data.userId
        });
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = 'Failed to create user. ';

      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        errorMessage = 'User creation is taking longer than expected. The user may have been created successfully. Please check the user list and try refreshing the page.';
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.response?.status === 409) {
        errorMessage += 'A user with this email already exists.';
      } else if (error.response?.status === 400) {
        errorMessage += 'Invalid user data. Please check all fields.';
      } else {
        errorMessage += error.message || 'Please try again later.';
      }

      setSubmitError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessModalClose = (action) => {
    setShowSuccessModal(false);
    if (action === 'manage') {
      navigate('/admin/user-management');
    } else if (action === 'profile' && createdUser) {
      // Navigate to the admin profile editor for the newly created user
      navigate(`/admin/user-profile/${createdUser.userId}`);
    } else if (action === 'add-another') {
      // Reset form and stay on page
      setCreatedUser(null);
      setSubmitError(null);
    }
  };

  return (
    <Container fluid className="landing-page admin-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="">
                <img src={userPuzzle} alt="" style={{ width: '140px', height: '140px' }} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Add New User</span>
              <span className="blgListSubTitle">Create New User Account</span>
              <p className="landing-subheading-text">
                Create a new user account with basic information. After creation, the user
                can complete their profile details and the system will automatically set up
                the appropriate profiles based on their role.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  onClick={() => navigate('/admin/user-management')}
                  size="large"
                  className="landing-goto-button"
                  icon="arrow left"
                  content="Back to User Management"
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        {/* Information Message */}
        <Message info icon>
          <Icon name="info circle" />
          <Message.Content>
            <Message.Header>About User Creation</Message.Header>
            This form creates the basic user account. The system will automatically generate
            the appropriate profile(s) based on the selected user type. Users can complete
            their detailed profile information after their first login.
          </Message.Content>
        </Message>

        {/* User Type Examples */}
        <Header as="h2" dividing>
          <Icon name="users" />
          <Header.Content>
            User Types
            <Header.Subheader>Select the appropriate role for the new user</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={3} stackable>
          {userTypeData.map((type) => (
            <Card key={type.value}>
              <Card.Content >
                <div style={{ display: "block", marginTop: "40px", maxHeight: "60px" }}><Image src={type.image} size="tiny" centered verticalAlign='bottom' /></div>
                <Card.Header textAlign="center" style={{ marginTop: '-10px' }}>
                  {/* <Icon name={type.icon} color={type.color} /> */}
                  {type.label}
                </Card.Header>
                <Card.Description>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.5em' }}>{type.description}</p>
                  <List bulleted>
                    {type.capabilities.map((capability, index) => (
                      <List.Item key={index}>{capability}</List.Item>
                    ))}
                  </List>
                  <Divider />
                  <Header as="h5">Example User:</Header>
                  <p><strong>{type.exampleUser.name}</strong><br />
                    {type.exampleUser.role}<br />
                    <em>Specialties: {type.exampleUser.specialties}</em></p>
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        {/* User Creation Form */}
        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <Icon name="edit" />
          <Header.Content>
            User Information
            <Header.Subheader>Enter the basic information for the new user</Header.Subheader>
          </Header.Content>
        </Header>

        {submitError && (
          <Message negative>
            <Message.Header>Error Creating User</Message.Header>
            <p>{submitError}</p>
          </Message>
        )}

        <Segment>
          <Formik
            initialValues={{
              email: '',
              password: '',
              confirmPassword: '',
              firstName: '',
              lastName: '',
              phone: '',
              userType: ''
            }}
            validationSchema={addUserValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, errors, touched, setFieldValue }) => (
              <Form>
                <Grid>
                  {/* Basic Information */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>Basic Information</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row>
                    <Grid.Column width={8}>
                      <FormikField
                        name="firstName"
                        component={FormikInput}
                        label="First Name"
                        placeholder="Enter first name"
                        fluid
                        error={touched.firstName && errors.firstName}
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <FormikField
                        name="lastName"
                        component={FormikInput}
                        label="Last Name"
                        placeholder="Enter last name"
                        fluid
                        error={touched.lastName && errors.lastName}
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row>
                    <Grid.Column width={8}>
                      <FormikField
                        name="email"
                        component={FormikInput}
                        label="Email Address"
                        placeholder="user@example.com"
                        type="email"
                        fluid
                        icon="mail"
                        error={touched.email && errors.email}
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <FormikField
                        name="phone"
                        component={FormikInput}
                        label="Phone Number (Optional)"
                        placeholder="Enter phone number"
                        type="tel"
                        fluid
                        icon="phone"
                        error={touched.phone && errors.phone}
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {/* User Type Selection */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>User Role</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row>
                    <Grid.Column width={16}>
                      <FormikField
                        name="userType"
                        component={FormikSelect}
                        label="User Type"
                        placeholder="Select user type"
                        options={userTypeData.map(type => ({
                          key: type.value,
                          text: type.label,
                          value: type.value,
                          icon: type.icon
                        }))}
                        fluid
                        error={touched.userType && errors.userType}
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {/* Password Section */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Header as="h4" dividing>Security</Header>
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row>
                    <Grid.Column width={8}>
                      <FormikField
                        name="password"
                        component={FormikInput}
                        label="Password"
                        placeholder="Enter password"
                        type="password"
                        fluid
                        icon="lock"
                        error={touched.password && errors.password}
                        onChange={(e) => handlePasswordChange(e, setFieldValue)}
                      />
                      {showPasswordStrength && (
                        <div style={{ marginTop: '0.5em' }}>
                          <small>Password Strength:</small>
                          <div style={{
                            height: '5px',
                            background: '#e0e0e0',
                            borderRadius: '5px',
                            marginTop: '0.25em'
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${passwordStrength}%`,
                              background: passwordStrength < 50 ? '#ff4444' :
                                passwordStrength < 75 ? '#ffaa00' : '#00aa00',
                              borderRadius: '5px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      )}
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <FormikField
                        name="confirmPassword"
                        component={FormikInput}
                        label="Confirm Password"
                        placeholder="Re-enter password"
                        type="password"
                        fluid
                        icon="lock"
                        error={touched.confirmPassword && errors.confirmPassword}
                      />
                    </Grid.Column>
                  </Grid.Row>

                  {/* Form Actions */}
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Divider />
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <Button
                          type="button"
                          onClick={() => navigate('/admin/user-management')}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          primary
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          <Icon name="plus" />
                          Create User
                        </Button>
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form>
            )}
          </Formik>
        </Segment>
      </Container>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => handleSuccessModalClose('manage')}
        size="small"
      >
        <Modal.Header>
          <Icon name="check circle" color="green" />
          User Created Successfully
        </Modal.Header>
        <Modal.Content>
          <Message success>
            <Message.Header>User account has been created!</Message.Header>
            <p>
              The user <strong>{createdUser?.name}</strong> ({createdUser?.email}) has been
              successfully created with the role of <strong>{createdUser?.primaryRole === 'admin' ? 'Administrator' :
                createdUser?.primaryRole === 'bookCreator' ? 'Book Creator' : 'Voice Actor'}</strong>.
            </p>
          </Message>
          <Segment>
            <Header as="h4">Next Steps:</Header>
            <List bulleted>
              <List.Item>The system has automatically created the appropriate profile(s) for this user</List.Item>
              <List.Item>The user can log in with their email and password</List.Item>
              <List.Item>They should complete their profile information after their first login</List.Item>
              {createdUser?.primaryRole === 'admin' && (
                <List.Item>As an administrator, they have access to all three profile types</List.Item>
              )}
              {createdUser?.primaryRole === 'bookCreator' && (
                <List.Item>As a book creator, they have access to book creator and voice actor profiles</List.Item>
              )}
              {createdUser?.primaryRole === 'voiceActor' && (
                <List.Item>As a voice actor, they have access to the voice actor profile</List.Item>
              )}
            </List>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => handleSuccessModalClose('add-another')}>
            <Icon name="plus" />
            Add Another User
          </Button>
          <Button color="blue" onClick={() => handleSuccessModalClose('profile')}>
            <Icon name="user circle" />
            Edit User Profile
          </Button>
          <Button primary onClick={() => handleSuccessModalClose('manage')}>
            <Icon name="users" />
            Back to User Management
          </Button>
        </Modal.Actions>
      </Modal>

      {AuthStdFooter()}
    </Container>
  );
};

export default AddUser;
